'use client';

import { useEffect } from 'react';
import { useSyncExternalStore } from 'react';

/**
 * The sheets a brand-new owner meets on his first minute inside the app, and
 * the order they are allowed to appear in.
 *
 * ── WHY A MODULE-LEVEL STORE ────────────────────────────────────────────────
 *
 * Same reasoning as `offline-surface.ts`, and the same two jobs.
 *
 * FIRST, ORDER. `UnlimitedOfferModal` lives in `AuthedShell` and decides
 * whether to show by asking the server; `WhatsAppRequiredPrompt` is rendered by
 * the authed layout beside it; the Get Started surface sits back in `AuthedShell`
 * and asks the server too. None of the three can see the other two, so any of
 * them could open at once — and two of them are undismissible, so "at once"
 * means the offer arriving underneath a sheet nobody can get past. The two-week
 * offer is the nicer thing to be handed first, and it is the one he can act on;
 * pairing comes after; the skills signpost last.
 *
 * SECOND, CHROME. `MobileNav` is mounted in the ROOT layout, nowhere near
 * either of them in the tree, so there is no provider that could sit above all
 * three without wrapping the whole app. The bar has no business showing under
 * an intro sheet: its tabs go nowhere the owner is allowed yet, and under an
 * undismissible sheet they are a row of controls that do nothing — which is the
 * exact complaint that made the offline surface hide it too.
 */

/**
 * Lowest number wins. The offer is the friendlier thing to hand him first,
 * pairing is the thing he cannot skip, and the skills signpost goes last —
 * it asks him to go and look at something, which is only a reasonable request
 * once the app behind the sheet is actually his to use.
 */
// `receptionist-upgrade` goes last: it is the only one of the four that is not
// part of a first minute in the app, and an owner still being walked in has
// no receptionist to upgrade yet. (`ReceptionistUpgradeSheet`, Sep 2026.)
// `get-started` sits straight after the two-week offer and BEFORE the WhatsApp
// prompt, because that is the order the flow puts them in: the offer is the
// reward for finishing onboarding, and Get Started is the first thing to DO.
//
// The WhatsApp prompt below it is now a backstop rather than the main ask —
// the short flow pairs inside onboarding — so it fires only for the accounts
// that came through the reserve arm or were waived.
const PRIORITY = {
  offer: 1,
  'get-started': 2,
  whatsapp: 3,
  // The receptionist is out of minutes (27 Oct 2026). Ahead of every announcement
  // because it is about calls being lost now, behind the first-minute surfaces
  // because an owner still being walked in has no receptionist to run out.
  'out-of-minutes': 4,
  'receptionist-upgrade': 5,
  // Existing users picking Mucka's character (CharacterGate, 4 Nov 2026). Must
  // pick, so ahead of every announcement; behind the first-minute surfaces and
  // a receptionist losing calls, which matter more than how Mucka sounds.
  character: 6,
  // Last of all: an announcement to people already using Mucka (Tom, 7 Oct
  // 2026). Anything that is part of somebody's first minute goes first.
  'web-access': 7,
  // "Put Mucka in your pocket" (GetAppSheet, 27 Oct 2026). Behind every
  // first-minute surface and announcement: it is for people already set up.
  'get-app': 8,
  // After every announcement: a reminder to share Mucka is the least urgent
  // thing anyone can be shown (12 Oct 2026).
  'share-earn': 9,
} as const;

export type IntroSurfaceId = keyof typeof PRIORITY;

const wanted = new Set<IntroSurfaceId>();
const listeners = new Set<() => void>();

function emit(): void {
  for (const fn of listeners) fn();
}

/**
 * The three functions below are the whole decision, exported so it can be
 * tested without mounting anything — the same reason `finishIntroGate` and
 * `micTapAction` are pure and exported. The hooks underneath are thin wrappers
 * that call them at the right moments in a component's life.
 */

/** This surface wants the screen. Idempotent. */
export function claimIntroSurface(id: IntroSurfaceId): void {
  if (wanted.has(id)) return;
  wanted.add(id);
  emit();
}

/** It no longer does — dismissed, or unmounted. This is what lets the next through. */
export function releaseIntroSurface(id: IntroSurfaceId): void {
  if (!wanted.delete(id)) return;
  emit();
}

/** Test-only reset, so one case cannot leak a claim into the next. */
export function resetIntroSurfaces(): void {
  wanted.clear();
  emit();
}

/** The surface that should be on screen right now, or null if none wants to be. */
export function topmostIntroSurface(): IntroSurfaceId | null {
  let best: IntroSurfaceId | null = null;
  for (const id of wanted) {
    if (best === null || PRIORITY[id] < PRIORITY[best]) best = id;
  }
  return best;
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

const getSnapshot = (): IntroSurfaceId | null => topmostIntroSurface();

/**
 * SSR renders the chrome as normal. Both surfaces decide on the client — the
 * offer asks the server, the sheet waits for a pairing read — so claiming
 * otherwise here would flash the nav bar away on every ordinary page load.
 */
const getServerSnapshot = (): IntroSurfaceId | null => null;

/**
 * Declare that this surface wants the screen, and learn whether it may have it.
 *
 * Returns false while something with a higher claim is up, so a caller renders
 * nothing rather than stacking. Deregisters on unmount, which is what lets the
 * next one through when the first is dismissed.
 */
export function useIntroSurface(id: IntroSurfaceId, wants: boolean): boolean {
  useEffect(() => {
    if (!wants) return;
    claimIntroSurface(id);
    return () => releaseIntroSurface(id);
  }, [id, wants]);

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot) === id;
}

/** True while any intro surface is on screen. Read by the chrome. */
export function useIntroSurfaceOpen(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot) !== null;
}
